import React, { Component } from 'react';
import Inherit from '../../../core/inherit';
import withNavigate from '../../../core/withNavigate';
import ContractController from './contract.controller';
import { Layout, TableSkeleton, AnalyticsSkeleton, ChannelFilter, PageHeader, StatCard, StatusTag, DetailFact } from '../../common';
import { Tag, Button } from 'antd';
import { FileText, Pencil, Calendar, CalendarClock, Store, DollarSign, Tags, TriangleAlert } from 'lucide-react';
import dayjs from 'dayjs';
import { ContractAnalytics, ContractStoreResults, ContractImageGallery } from './elements';
import { formatCurrency, formatNumber } from '../../../utils/format';
import { ALL_CHANNELS } from '../../../utils/storeChannel';
import { isFeatureEnabled } from '../../../company/session';
import { FEATURES } from '../../../company/features';

class ShelfContract extends Inherit(ContractController, Component) {
    state = {
        loading: true,
        contract: null,
        contractChecks: [],
        channel: ALL_CHANNELS
    };

    render() {
        const { contract, loading, channel } = this.state;

        if (loading) {
            return (
                <Layout current="contracts">
                    <PageHeader icon={<FileText />} title="Detalle de Contratación" description="Cargando..." />
                    <div className="space-y-4">
                        <AnalyticsSkeleton count={4} />
                        <TableSkeleton rows={6} columns={5} />
                    </div>
                </Layout>
            );
        }

        const { filteredContract, filteredChecks, analytics, contractType, isExpired, isClientUser, totalInvestment, channelCounts } = this.getViewModel();

        return (
            <Layout current="contracts">
                <PageHeader
                    icon={<FileText />}
                    title={contract?.name || 'Contratación'}
                    description="Detalle y resultados de la contratación"
                    meta={
                        <>
                            <DetailFact label="Tipo">
                                <Tag color={contractType?.color || 'default'} bordered={false} className="!m-0">
                                    {contractType?.label || contract?.type || 'N/A'}
                                </Tag>
                            </DetailFact>
                            {isExpired ? (
                                <StatusTag tone="warning" icon={<TriangleAlert />}>
                                    Expirado
                                </StatusTag>
                            ) : (
                                <StatusTag tone={contract?.active ? 'success' : 'danger'} dot>
                                    {contract?.active ? 'Activo' : 'Inactivo'}
                                </StatusTag>
                            )}
                            <DetailFact icon={<Calendar />} label="Creación">
                                {contract?.createdAt ? dayjs(contract.createdAt).format('DD/MM/YYYY') : 'N/A'}
                            </DetailFact>
                        </>
                    }
                    actions={
                        // Edit — hidden for client users
                        !isClientUser && (
                            <Button type="primary" icon={<Pencil />} onClick={() => this.handleEdit()}>
                                Editar Contratación
                            </Button>
                        )
                    }
                />

                <div className="space-y-4">
                    {/* Channel Filter — applies to every section below */}
                    <ChannelFilter
                        value={channel}
                        onChange={(next) => this.handleChannelChange(next)}
                        counts={channelCounts}
                        visibleStores={filteredContract?.stores?.length || 0}
                        totalStores={contract?.stores?.length || 0}
                    />

                    {/* Key facts & investment */}
                    <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 xl:grid-cols-4">
                        <StatCard label="Inversión Total" value={formatCurrency(totalInvestment)} icon={<DollarSign />} tone="success" hint="Suma de los pisos visibles" />
                        <StatCard
                            label="Precio por Piso"
                            value={formatCurrency(contract?.price || 0)}
                            icon={<Tags />}
                            tone="brand"
                            hint="Precio general de la contratación"
                        />
                        <StatCard label="Pisos Asignados" value={formatNumber(analytics.totalStores)} icon={<Store />} tone="violet" hint="Pisos de venta en la contratación" />
                        <StatCard
                            label="Vencimiento"
                            value={contract?.expirationDate ? dayjs(contract.expirationDate).format('DD/MM/YYYY') : 'N/A'}
                            valueTone={isExpired ? 'danger' : undefined}
                            icon={<CalendarClock />}
                            tone={isExpired ? 'danger' : 'info'}
                            hint={isExpired ? 'Contratación expirada' : 'Fecha de vencimiento'}
                        />
                    </div>

                    {/* Compliance */}
                    <ContractAnalytics analytics={analytics} contract={filteredContract} />

                    {/* Store Results Section */}
                    <ContractStoreResults
                        contract={filteredContract}
                        contractChecks={filteredChecks}
                        navigateStore={this.navigateStore.bind(this)}
                        onStorePriceChange={this.updateStorePrice.bind(this)}
                        canEditPrices={!isClientUser && isFeatureEnabled(FEATURES.TRADE_STORE_PRICES)}
                    />

                    {/* Image Gallery Section */}
                    <ContractImageGallery contract={filteredContract} contractChecks={filteredChecks} />
                </div>
            </Layout>
        );
    }
}

export default withNavigate(ShelfContract);
